import { CircleCheck, XCircle } from "lucide-react";

import { PayoutStatus } from "@/api/admin/fundings";
import { BasicModal } from "@/components/molecules";
import { cn } from "@/lib/utils";
import { commonIconProps20 } from "@/utils/constants";

import { useUpdateApplicationStatus } from "./useUpdateApplicationStatus";

interface FundingActionsProps {
  id: string;
  canApprove: boolean;
  canReject: boolean;
  queryKey?: string | string[];
}

interface FundingActionItemProps {
  isPending: boolean;
  onAction: (status: PayoutStatus) => void;
  classes?: string;
  disabled?: boolean;
}

export const ApproveAction = ({
  isPending,
  onAction,
  classes,
  disabled,
}: FundingActionItemProps) => (
  <BasicModal
    title="Approve Funding"
    btnTitle="Confirm Approve"
    isPending={isPending}
    disabled={disabled}
    action={() => onAction(PayoutStatus.APPROVED)}
    body={
      <p className="text-sm text-gray-950 mb-2">
        Are you sure you want to approve this application?
      </p>
    }
    trigger={
      <div className={cn("flex items-center gap-3 cursor-pointer", classes)}>
        <CircleCheck {...commonIconProps20} color="#65A30D" />
        <span>Approve</span>
      </div>
    }
    trigerClasses="w-full"
  />
);

export const RejectAction = ({
  isPending,
  onAction,
  classes,
  disabled,
}: FundingActionItemProps) => (
  <BasicModal
    title="Reject Funding"
    btnTitle="Confirm Reject"
    isPending={isPending}
    disabled={disabled}
    action={() => onAction(PayoutStatus.DECLINED)}
    body={
      <p className="text-sm text-gray-950 mb-2">
        Are you sure you want to reject this application?
      </p>
    }
    trigger={
      <div className={cn("flex items-center gap-3 cursor-pointer", classes)}>
        <XCircle {...commonIconProps20} color="#DC2626" />
        <span>Reject</span>
      </div>
    }
    trigerClasses="w-full"
  />
);

export const FundingActions = ({
  id,
  canApprove,
  canReject,
  queryKey,
}: FundingActionsProps) => {
  const { handleChangeStatus, isPending } = useUpdateApplicationStatus(
    id,
    queryKey
  );

  return (
    <div className="flex items-center gap-2">
      {canApprove && (
        <ApproveAction
          isPending={isPending}
          onAction={handleChangeStatus}
          classes="py-2.5 px-4 rounded-[6px] font-bold text-sm bg-[#E5F1D4] text-[#65A30D]"
        />
      )}

      {canReject && (
        <RejectAction
          isPending={isPending}
          onAction={handleChangeStatus}
          classes="py-2.5 px-4 rounded-[6px] font-bold text-sm bg-[#FEE2E2] text-[#DC2626] "
        />
      )}
    </div>
  );
};
